import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { fromBlob } from 'geotiff';
import proj4 from 'proj4';
import {
  Building2, ChevronRight, ChevronDown, Layers, MapPin, Plus, Pencil,
  Trash2, X, AlertCircle, Sprout, Calendar, BarChart2, Globe, Map as MapIcon,
  Expand, ArrowLeft, RefreshCw, ImagePlus, Upload, Download, FlaskConical,
  ZoomIn, ZoomOut, RotateCcw, CheckCircle2, Loader2, Leaf,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';
import type { EmpFinca, EmpLote, EmpBloque, TipoBloque, CicloBloque, EstadoCiclo } from '../../types/empacadora';
import GeoMapPreview from '../../components/GeoMapPreview';
import ParcelProjectMap from '../../components/ParcelProjectMap';
import MapaLoteInteractivo from '../../components/MapaLoteInteractivo';
import { getMapaLote } from '../../data/mapasLotes';
import { invocarMotorPythonBloquesPoligonoArchivo, type MotorPythonBatchResponse, type MotorPythonBatchBlock } from './deteccionService';
import { ConteoPlantasV8Page } from './ConteoPlantasV8Page';
import { ConteoCalibradoPage } from './ConteoCalibradoPage';
import { LoteWorkspace } from './LoteWorkspace';
import { BloqueWorkspace } from './BloqueWorkspace';
import {
  inputCls, selectCls, labelCls, btnPrimary, btnSecondary, btnDanger,
  cardCls, errorCls, pageTitleCls, pageSubtitleCls, sectionLabelCls,
} from '../../components/ui';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtHa(v?: number | null) {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(2)} ha`;
}

function fmtFecha(s?: string | null) {
  if (!s) return '—';
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function diasDesde(fechaStr?: string | null): number | null {
  if (!fechaStr) return null;
  const diff = Date.now() - new Date(fechaStr + 'T00:00:00').getTime();
  return Math.floor(diff / 86_400_000);
}

const CICLO_LABEL: Record<CicloBloque, string> = { PC: 'Primera Cosecha', RC: 'Segunda Cosecha', RC2: 'Tercera Cosecha' };
const TIPO_BLOQUE_LABEL: Record<TipoBloque, string> = {
  siembra: 'Siembra', camino: 'Camino', proteccion: 'Zona protegida', otro: 'Otro',
};

// ─── Modal base ──────────────────────────────────────────────────────────────

function Modal({ title, onClose, children, wide = false }: {
  title: string; onClose: () => void; children: React.ReactNode; wide?: boolean;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div
        className="relative w-full rounded-xl shadow-2xl overflow-hidden flex flex-col"
        style={{
          maxWidth: wide ? '52rem' : '36rem',
          background: 'var(--surface-overlay)', border: '1px solid var(--line)', maxHeight: '92vh',
        }}
      >
        <div className="flex items-center justify-between px-5 py-3.5 shrink-0"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{title}</span>
          <button onClick={onClose} style={{ color: 'var(--ink-faint)' }}><X size={16} /></button>
        </div>
        <div className="overflow-y-auto flex-1 p-5">{children}</div>
      </div>
    </div>,
    document.body
  );
}

// ─── GeoTIFF helpers ─────────────────────────────────────────────────────────

function inferEpsg(geoKeys: any): number {
  const proj = Number(geoKeys?.ProjectedCSTypeGeoKey || 0);
  const geog = Number(geoKeys?.GeographicTypeGeoKey || 0);
  if (proj > 0) return proj;
  if (geog > 0) return geog;
  const m = String(geoKeys?.GTCitationGeoKey || '').match(/UTM\s*zone\s*(\d{1,2})\s*([NS])/i);
  if (m) { const z = Number(m[1]); return m[2].toUpperCase() === 'S' ? 32700 + z : 32600 + z; }
  return 0;
}

function toWgs84(epsg: number, x: number, y: number): [number, number] {
  if (epsg === 4326) return [x, y];
  let from = '';
  if (epsg >= 32601 && epsg <= 32660) from = `+proj=utm +zone=${epsg - 32600} +datum=WGS84 +units=m +no_defs`;
  else if (epsg >= 32701 && epsg <= 32760) from = `+proj=utm +zone=${epsg - 32700} +south +datum=WGS84 +units=m +no_defs`;
  else throw new Error(`CRS EPSG:${epsg} no soportado. Exportá el GeoTIFF en WGS84 o UTM.`);
  const [lng, lat] = proj4(from, 'WGS84', [x, y]);
  return [lng, lat];
}

async function extractGeoTiffBounds(file: File): Promise<{ url: string; bounds: [[number, number], [number, number]] }> {
  const tiff = await fromBlob(file);
  const img = await tiff.getImage();
  const geoKeys = img.getGeoKeys();
  const epsg = inferEpsg(geoKeys);
  if (!epsg) throw new Error('El GeoTIFF no tiene información de proyección (CRS). Exportalo con coordenadas.');

  const bbox = img.getBoundingBox(); // [minX, minY, maxX, maxY]
  const [minLng, minLat] = toWgs84(epsg, bbox[0], bbox[1]);
  const [maxLng, maxLat] = toWgs84(epsg, bbox[2], bbox[3]);

  // Leer píxeles y renderizar en canvas → PNG (Leaflet no soporta GeoTIFF directo)
  const MAX_DIM = 3000;
  const srcW = img.getWidth();
  const srcH = img.getHeight();
  const scale = Math.min(1, MAX_DIM / Math.max(srcW, srcH));
  const outW = Math.round(srcW * scale);
  const outH = Math.round(srcH * scale);

  const rasters = await img.readRasters({ width: outW, height: outH, interleave: true });
  const data = rasters as unknown as Uint8Array | Uint16Array | Float32Array;
  const samplesPerPixel = img.getSamplesPerPixel();

  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d')!;
  const imgData = ctx.createImageData(outW, outH);

  for (let i = 0; i < outW * outH; i++) {
    if (samplesPerPixel >= 3) {
      imgData.data[i * 4 + 0] = Math.min(255, Math.round(Number(data[i * samplesPerPixel + 0])));
      imgData.data[i * 4 + 1] = Math.min(255, Math.round(Number(data[i * samplesPerPixel + 1])));
      imgData.data[i * 4 + 2] = Math.min(255, Math.round(Number(data[i * samplesPerPixel + 2])));
      imgData.data[i * 4 + 3] = samplesPerPixel >= 4 ? Math.min(255, Math.round(Number(data[i * samplesPerPixel + 3]))) : 255;
    } else {
      const v = Math.min(255, Math.round(Number(data[i])));
      imgData.data[i * 4 + 0] = v;
      imgData.data[i * 4 + 1] = v;
      imgData.data[i * 4 + 2] = v;
      imgData.data[i * 4 + 3] = 255;
    }
  }
  ctx.putImageData(imgData, 0, 0);
  const url = canvas.toDataURL('image/png');

  return { url, bounds: [[minLat, minLng], [maxLat, maxLng]] };
}

// ─── Vista completa: Ortofoto de Dron ────────────────────────────────────────

function OrtofotoView({
  titulo,
  initialGeojson,
  loteId,
  empresaId,
  onConfirm,
  onCancel,
}: {
  titulo: string;
  initialGeojson: any;
  loteId?: string;
  empresaId?: number;
  onConfirm: (geojson: any, rasterPngUrl?: string, rasterBounds?: [[number,number],[number,number]]) => void;
  onCancel: () => void;
}) {
  const [raster, setRaster] = useState<{ url: string; bounds: [[number, number], [number, number]] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [geojson, setGeojson] = useState<any>(initialGeojson ?? null);
  const [drawResetKey, setDrawResetKey] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const tifFileRef = useRef<File | null>(null);

  async function handleFile(file: File) {
    setLoading(true); setLoadErr('');
    tifFileRef.current = file;
    try {
      const result = await extractGeoTiffBounds(file);
      setRaster(result);
    } catch (e: any) {
      setLoadErr(e?.message ?? 'Error al leer el GeoTIFF.');
    }
    setLoading(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  async function handleConfirm() {
    if (!geojson) return;
    // Guardar PNG en localStorage para recuperarlo en AnalisisLoteView
    if (loteId && raster) {
      try {
        localStorage.setItem(`raster_png_${loteId}`, raster.url);
        localStorage.setItem(`raster_bounds_${loteId}`, JSON.stringify(raster.bounds));
      } catch {
        // localStorage lleno — no es crítico
      }
    }
    onConfirm(geojson, raster?.url, raster?.bounds);
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: 'var(--surface-base)' }}>
      <div className="flex items-center gap-4 px-5 py-3 shrink-0" style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
        <button onClick={onCancel} className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm"
          style={{ borderColor: 'var(--line)', background: 'var(--surface-overlay)', color: 'var(--ink-muted)' }}>
          <ArrowLeft size={15} /> Volver
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold truncate" style={{ color: 'var(--ink)' }}>{titulo}</h1>
          <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
            Cargá un GeoTIFF georreferenciado y dibujá el polígono sobre él
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {raster && (
            <button type="button" onClick={() => { setRaster(null); setLoadErr(''); tifFileRef.current = null; }}
              className="text-xs px-3 py-1.5 rounded-lg border"
              style={{ borderColor: 'var(--line)', color: 'var(--ink-faint)' }}>
              Cambiar imagen
            </button>
          )}
          <button type="button"
            onClick={() => { setGeojson(null); setDrawResetKey(k => k + 1); }}
            className="text-xs px-3 py-1.5 rounded-lg border"
            style={{ borderColor: 'var(--tone-warn-border)', color: 'var(--tone-warn-text)', background: 'var(--tone-warn-bg)' }}
            disabled={!geojson}>
            Limpiar polígono
          </button>
          <button type="button" onClick={handleConfirm}
            className="text-sm px-4 py-1.5 rounded-lg font-semibold inline-flex items-center gap-2"
            style={{ background: 'var(--emp-accent)', color: '#fff', opacity: !geojson || uploading ? 0.5 : 1 }}
            disabled={!geojson || uploading}>
            {uploading ? <><Loader2 size={14} className="animate-spin" /> Guardando...</> : 'Usar este polígono'}
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col p-4 gap-4">
        {!raster && (
          <div className="flex-1 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed cursor-pointer transition-colors"
            style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}>
            <input ref={fileRef} type="file" accept=".tif,.tiff" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            {loading ? (
              <>
                <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mb-4"
                  style={{ borderColor: 'var(--emp-accent)', borderTopColor: 'transparent' }} />
                <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Procesando GeoTIFF...</p>
              </>
            ) : (
              <>
                <Upload size={40} style={{ color: 'var(--ink-faint)', opacity: 0.5, marginBottom: 16 }} />
                <p className="text-base font-semibold mb-1" style={{ color: 'var(--ink)' }}>Arrastrá o hacé clic para cargar el GeoTIFF</p>
                <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>DJI Terra · Pix4D · OpenDroneMap — debe tener CRS (WGS84 o UTM)</p>
                {loadErr && (
                  <div className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg"
                    style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
                    <AlertCircle size={14} style={{ color: 'var(--tone-warn-text)' }} />
                    <span className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>{loadErr}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {raster && (
          <div className="flex-1 min-h-0 rounded-2xl overflow-hidden" style={{ border: '1px solid var(--line)' }}>
            <GeoMapPreview
              geojson={geojson} height="100%" maxWidthClassName="max-w-full"
              polygonColor="#16a34a" editable drawResetKey={drawResetKey}
              onDrawCommit={setGeojson}
              rasterOverlay={{ url: raster.url, bounds: raster.bounds, opacity: 0.9 }}
            />
          </div>
        )}
        {raster && !geojson && (
          <p className="text-center text-sm shrink-0" style={{ color: 'var(--ink-faint)' }}>
            Clic para marcar vértices · doble clic para cerrar · flechas del teclado para desplazarte
          </p>
        )}
        {raster && geojson && (
          <p className="text-center text-sm shrink-0" style={{ color: 'var(--tone-success-text)' }}>
            ✓ Polígono listo — presioná "Usar este polígono" para aplicarlo
          </p>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── Vista de Análisis con Motor V5 ──────────────────────────────────────────

type BloqueDraw = { bloque_num: number; puntos: [number, number][] };

function fmtNum(v?: number | null, dec = 0) {
  if (v == null || !Number.isFinite(v)) return '—';
  return v.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function kpiColor(kpi?: number) {
  if (kpi == null) return 'var(--ink-faint)';
  if (kpi >= 90) return 'var(--tone-success-text)';
  if (kpi >= 70) return 'var(--tone-warn-text)';
  return '#f87171';
}

function AnalisisLoteView({
  nombre,
  loteId,
  rasterPngUrl,
  rasterBounds,
  geojsonLote,
  empresaId,
  onClose,
}: {
  nombre: string;
  loteId: string;
  rasterPngUrl?: string;
  rasterBounds?: [[number,number],[number,number]];
  geojsonLote?: any;
  empresaId: number;
  onClose: () => void;
}) {
  // Imagen — intenta recuperar PNG guardado en localStorage
  const [previewUrl, setPreviewUrl] = useState<string>(() => {
    if (rasterPngUrl) return rasterPngUrl;
    try { return localStorage.getItem(`raster_png_${loteId}`) ?? ''; } catch { return ''; }
  });
  const [tifFile, setTifFile] = useState<File | null>(null);
  const [loadingImg, setLoadingImg] = useState(false);
  const [imgErr, setImgErr] = useState('');
  const tifInputRef = useRef<HTMLInputElement>(null);

  // Dibujo de bloques
  const [points, setPoints] = useState<[number, number][]>([]);
  const [blocks, setBlocks] = useState<BloqueDraw[]>([]);
  const [zoom, setZoom] = useState(1);
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef<{ x: number; y: number; sl: number; st: number } | null>(null);
  const movedRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Motor
  const [gsd, setGsd] = useState('0.05');
  const [camas, setCamas] = useState('12');
  const [running, setRunning] = useState(false);
  const [motorErr, setMotorErr] = useState('');
  const [resultado, setResultado] = useState<MotorPythonBatchResponse | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const nextBlock = useMemo(() =>
    blocks.length === 0 ? 1 : Math.max(...blocks.map(b => b.bloque_num)) + 1,
    [blocks]
  );

  // Teclado: Backspace deshace punto, Ctrl+Z deshace punto
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault(); setPoints(p => p.slice(0, -1));
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault(); setPoints(p => p.slice(0, -1));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function cargarTif(file: File) {
    setLoadingImg(true); setImgErr('');
    try {
      const isTiff = /\.(tif|tiff)$/i.test(file.name);
      if (isTiff) {
        const { url } = await extractGeoTiffBounds(file);
        setPreviewUrl(url);
        setTifFile(file);
      } else {
        setPreviewUrl(URL.createObjectURL(file));
        setTifFile(file);
      }
    } catch (e: any) {
      setImgErr(e?.message ?? 'Error al leer el archivo.');
    }
    setLoadingImg(false);
  }

  function onImgClick(e: React.MouseEvent<HTMLImageElement>) {
    if (movedRef.current) return;
    if (!imgRef.current) return;
    const rect = imgRef.current.getBoundingClientRect();
    const nx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const ny = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    setPoints(p => [...p, [nx, ny]]);
  }

  function onPanStart(e: React.MouseEvent<HTMLDivElement>) {
    if (zoom <= 1) return;
    e.preventDefault(); movedRef.current = false;
    if (!containerRef.current) return;
    panRef.current = { x: e.clientX, y: e.clientY, sl: containerRef.current.scrollLeft, st: containerRef.current.scrollTop };
    setIsPanning(true);
  }
  function onPanMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!isPanning || !panRef.current || !containerRef.current) return;
    e.preventDefault();
    const dx = e.clientX - panRef.current.x;
    const dy = e.clientY - panRef.current.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) movedRef.current = true;
    containerRef.current.scrollLeft = panRef.current.sl - dx;
    containerRef.current.scrollTop = panRef.current.st - dy;
  }
  function onPanEnd() {
    panRef.current = null; setIsPanning(false);
    window.setTimeout(() => { movedRef.current = false; }, 0);
  }

  function addBlock() {
    if (points.length < 3) { setMotorErr('Marcá al menos 3 puntos para cerrar un bloque.'); return; }
    setBlocks(prev => [...prev, { bloque_num: nextBlock, puntos: [...points] }]);
    setPoints([]); setMotorErr('');
  }

  function removeBlock(num: number) {
    setBlocks(prev => prev.filter(b => b.bloque_num !== num));
  }

  function pointsToSvgPoly(pts: [number,number][]) {
    return pts.map(p => `${p[0]*100},${p[1]*100}`).join(' ');
  }

  async function runAnalisis() {
    if (!tifFile && !previewUrl) { setMotorErr('Cargá el archivo TIF del dron primero.'); return; }
    if (!tifFile) { setMotorErr('El motor necesita el archivo TIF original para analizar. Cargalo con el botón de arriba.'); return; }
    if (blocks.length === 0) { setMotorErr('Dibujá al menos un bloque de siembra.'); return; }
    setRunning(true); setMotorErr(''); setResultado(null);
    try {
      const gsdVal = Math.max(0.01, Number(gsd) || 0.05);
      const camasVal = Math.max(1, Math.round(Number(camas) || 12));
      const res = await invocarMotorPythonBloquesPoligonoArchivo(
        { minArea: 20, maxArea: 800 },
        blocks.map(b => ({ bloque_num: b.bloque_num, puntos: b.puntos })),
        tifFile,
        null,
        loteId || 'upload',
        { areaMode: 'fixed_gsd', gsdM: gsdVal, detectMode: 'conteo5_camas', expectedBeds: camasVal }
      );
      setResultado(res);
    } catch (e: any) {
      setMotorErr(e?.message || String(e));
    } finally {
      setRunning(false);
    }
  }

  async function guardarResultados() {
    if (!resultado?.bloques?.length) return;
    setSaving(true);
    try {
      for (const b of resultado.bloques) {
        if (b.bloque_num == null || !b.conteo) continue;
        await supabase.from('emp_bloques').upsert({
          empresa_id: empresaId,
          lote_id: loteId,
          num: b.bloque_num,
          tipo: 'siembra',
          area_ha: b.area_ha,
          plant_count: b.conteo,
          conteo_detectado_at: new Date().toISOString(),
          conteo_modo: 'rgb',
          conteo_resultado_payload: b,
          activo: true,
        }, { onConflict: 'empresa_id,lote_id,num' });
      }
      setSaved(true);
    } catch (e: any) {
      setMotorErr(e?.message ?? 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  }

  const totalPlantas = resultado?.bloques?.reduce((s, b) => s + (b.conteo ?? 0), 0) ?? 0;
  const totalHa = resultado?.bloques?.reduce((s, b) => s + (b.area_ha ?? 0), 0) ?? 0;
  const densidadProm = totalHa > 0 ? totalPlantas / totalHa : 0;
  const kpiProm = resultado?.bloques?.length
    ? resultado.bloques.reduce((s, b) => s + (b.kpi ?? 0), 0) / resultado.bloques.length
    : 0;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex flex-col" style={{ background: 'var(--surface-base)' }}>

      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-5 py-2.5 shrink-0"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
        <button onClick={onClose} className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm shrink-0"
          style={{ borderColor: 'var(--line)', color: 'var(--ink-muted)' }}>
          <ArrowLeft size={14} /> Volver
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-base truncate" style={{ color: 'var(--ink)' }}>
            <FlaskConical size={15} className="inline mr-1.5" style={{ color: 'var(--emp-accent-txt)' }} />
            Análisis · {nombre}
          </h1>
          <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>
            Motor V5 · Dibujá los bloques de siembra y lanzá el conteo de plantas
          </p>
        </div>
        {/* Controles TIF */}
        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={() => tifInputRef.current?.click()}
            className="text-xs px-3 py-1.5 rounded-lg border inline-flex items-center gap-1.5"
            style={{ borderColor: 'var(--tone-info-border)', color: 'var(--tone-info-text)', background: 'var(--tone-info-bg)' }}>
            <Upload size={12} /> {tifFile ? tifFile.name.slice(0, 20) + '…' : 'Cargar TIF del dron'}
          </button>
          <input ref={tifInputRef} type="file" accept=".tif,.tiff,.png,.jpg" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) cargarTif(f); }} />
        </div>
      </div>

      {/* ── Body: imagen + controles lado a lado ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* Panel izquierdo: imagen */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0 p-3 gap-2">

          {/* Toolbar zoom + bloque */}
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <div className="flex items-center gap-1 rounded-lg border px-2 py-1"
              style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <button onClick={() => setZoom(z => Math.min(6, +(z + 0.25).toFixed(2)))} className="p-1 rounded hover:opacity-70"><ZoomIn size={14} style={{ color: 'var(--ink-muted)' }} /></button>
              <span className="text-xs w-12 text-center font-mono" style={{ color: 'var(--ink-faint)' }}>{(zoom * 100).toFixed(0)}%</span>
              <button onClick={() => setZoom(z => Math.max(0.4, +(z - 0.25).toFixed(2)))} className="p-1 rounded hover:opacity-70"><ZoomOut size={14} style={{ color: 'var(--ink-muted)' }} /></button>
              <button onClick={() => { setZoom(1); if (containerRef.current) { containerRef.current.scrollLeft = 0; containerRef.current.scrollTop = 0; } }} className="p-1 rounded hover:opacity-70"><RotateCcw size={13} style={{ color: 'var(--ink-muted)' }} /></button>
            </div>
            <div className="flex items-center gap-1 rounded-lg border px-2 py-1"
              style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>
              <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{points.length} punto{points.length !== 1 ? 's' : ''}</span>
              {points.length >= 3 && (
                <button onClick={addBlock}
                  className="text-xs px-2 py-0.5 rounded font-semibold ml-1"
                  style={{ background: 'var(--emp-accent)', color: '#fff' }}>
                  + Bloque B{nextBlock}
                </button>
              )}
              {points.length > 0 && (
                <button onClick={() => setPoints([])} className="text-xs px-2 py-0.5 rounded ml-1"
                  style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}>
                  Limpiar
                </button>
              )}
            </div>
            <span className="text-xs ml-auto" style={{ color: 'var(--ink-faint)' }}>
              {zoom > 1 ? 'Clic: punto · Arrastrar: mover · Backspace: deshacer' : 'Clic: punto · Backspace: deshacer'}
            </span>
          </div>

          {/* Sin TIF y sin polígono */}
          {!previewUrl && !loadingImg && !geojsonLote && (
            <div className="flex-1 min-h-0 rounded-xl flex flex-col items-center justify-center gap-3"
              style={{ border: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
              <ImagePlus size={48} style={{ color: 'var(--ink-faint)', opacity: 0.3 }} />
              <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Cargá el TIF del dron para comenzar</p>
              {imgErr && <p className="text-xs px-3 py-1 rounded" style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>{imgErr}</p>}
            </div>
          )}

          {/* Mapa base (cuando hay polígono pero no TIF) */}
          {!previewUrl && !loadingImg && geojsonLote && (
            <div className="flex-1 min-h-0 rounded-xl overflow-hidden mb-2" style={{ border: '1px solid var(--line)', minHeight: 200 }}>
              <GeoMapPreview
                geojson={geojsonLote}
                height="100%"
                maxWidthClassName="max-w-full"
                polygonColor="#a78bfa"
                editable={false}
              />
            </div>
          )}

          {/* Canvas imagen TIF */}
          <div ref={containerRef}
            className="flex-1 min-h-0 rounded-xl overflow-auto relative"
            style={{ border: '1px solid var(--line)', background: '#0a0f1a', cursor: isPanning ? 'grabbing' : zoom > 1 ? 'grab' : 'crosshair', display: previewUrl || loadingImg ? 'block' : 'none' }}
            onMouseDown={onPanStart} onMouseMove={onPanMove} onMouseUp={onPanEnd} onMouseLeave={onPanEnd}>
            {!previewUrl && !loadingImg && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <ImagePlus size={48} style={{ color: 'var(--ink-faint)', opacity: 0.3 }} />
                <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Cargá el TIF del dron para comenzar</p>
                {imgErr && <p className="text-xs px-3 py-1 rounded" style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)' }}>{imgErr}</p>}
              </div>
            )}
            {loadingImg && (
              <div className="absolute inset-0 flex items-center justify-center gap-3">
                <Loader2 size={32} className="animate-spin" style={{ color: 'var(--emp-accent)' }} />
                <span className="text-sm" style={{ color: 'var(--ink-faint)' }}>Procesando imagen...</span>
              </div>
            )}
            {previewUrl && !loadingImg && (
              <div className="relative inline-block" style={{ width: `${zoom * 100}%`, minWidth: '100%' }}>
                <img ref={imgRef} src={previewUrl} alt="ortofoto"
                  className="w-full block select-none"
                  draggable={false}
                  onClick={onImgClick}
                  style={{ cursor: isPanning ? 'grabbing' : zoom > 1 ? 'grab' : 'crosshair' }}
                />
                {/* SVG overlay bloques + puntos activos */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {/* Bloques guardados */}
                  {blocks.map(b => (
                    <g key={b.bloque_num}>
                      <polygon points={pointsToSvgPoly(b.puntos)}
                        fill="rgba(16,163,74,0.15)" stroke="#16a34a" strokeWidth="0.3" />
                      <text x={b.puntos.reduce((s,p)=>s+p[0],0)/b.puntos.length*100}
                        y={b.puntos.reduce((s,p)=>s+p[1],0)/b.puntos.length*100}
                        fontSize="2.5" fill="#4ade80" textAnchor="middle" dominantBaseline="middle"
                        fontWeight="bold" style={{ filter: 'drop-shadow(0 0 2px #000)' }}>
                        B{b.bloque_num}
                      </text>
                    </g>
                  ))}
                  {/* Puntos en progreso */}
                  {points.length > 1 && (
                    <polyline points={points.map(p=>`${p[0]*100},${p[1]*100}`).join(' ')}
                      fill="none" stroke="#f59e0b" strokeWidth="0.3" strokeDasharray="1,0.5" />
                  )}
                  {points.map((p, i) => (
                    <circle key={i} cx={p[0]*100} cy={p[1]*100} r="0.7" fill="#f59e0b" stroke="#fff" strokeWidth="0.2" />
                  ))}
                  {points.length >= 3 && (
                    <line x1={points[points.length-1][0]*100} y1={points[points.length-1][1]*100}
                      x2={points[0][0]*100} y2={points[0][1]*100}
                      stroke="#f59e0b" strokeWidth="0.2" strokeDasharray="1,0.5" />
                  )}
                </svg>
              </div>
            )}
          </div>
        </div>

        {/* Panel derecho: controles + resultados */}
        <div className="w-80 shrink-0 flex flex-col min-h-0 border-l overflow-y-auto"
          style={{ borderColor: 'var(--line)', background: 'var(--surface-raised)' }}>

          {/* Parámetros */}
          <div className="p-4 space-y-3 shrink-0" style={{ borderBottom: '1px solid var(--line-dim)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>Parámetros</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] mb-1 block" style={{ color: 'var(--ink-faint)' }}>GSD (m/px)</label>
                <input className={inputCls} type="number" step="0.01" min="0.01" value={gsd}
                  onChange={e => setGsd(e.target.value)} />
              </div>
              <div>
                <label className="text-[11px] mb-1 block" style={{ color: 'var(--ink-faint)' }}>Camas esperadas</label>
                <input className={inputCls} type="number" step="1" min="1" value={camas}
                  onChange={e => setCamas(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Bloques dibujados */}
          <div className="p-4 space-y-2 shrink-0" style={{ borderBottom: '1px solid var(--line-dim)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--ink-faint)' }}>
              Bloques ({blocks.length})
            </p>
            {blocks.length === 0 ? (
              <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Dibujá bloques en la imagen</p>
            ) : (
              blocks.map(b => (
                <div key={b.bloque_num} className="flex items-center justify-between rounded px-2 py-1"
                  style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line-dim)' }}>
                  <span className="text-xs font-medium" style={{ color: 'var(--emp-accent-txt)' }}>
                    Bloque B{b.bloque_num}
                  </span>
                  <span className="text-[10px]" style={{ color: 'var(--ink-faint)' }}>{b.puntos.length} pts</span>
                  <button onClick={() => removeBlock(b.bloque_num)} className="p-1"
                    style={{ color: 'var(--ink-faint)' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
                    <X size={12} />
                  </button>
                </div>
              ))
            )}
            {blocks.length > 0 && (
              <button onClick={() => { setBlocks([]); setPoints([]); setResultado(null); setSaved(false); }}
                className="text-[11px] px-2 py-1 rounded w-full mt-1"
                style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}>
                Limpiar todo
              </button>
            )}
          </div>

          {/* Botón analizar */}
          <div className="p-4 shrink-0" style={{ borderBottom: '1px solid var(--line-dim)' }}>
            {motorErr && (
              <div className="flex gap-2 p-2 rounded mb-3"
                style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
                <AlertCircle size={13} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
                <p className="text-xs" style={{ color: 'var(--tone-warn-ink)' }}>{motorErr}</p>
              </div>
            )}
            <button onClick={runAnalisis} disabled={running || blocks.length === 0 || !tifFile}
              className="w-full py-2.5 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2 transition-opacity"
              style={{ background: 'var(--emp-accent)', color: '#fff', opacity: running || blocks.length === 0 || !tifFile ? 0.5 : 1 }}>
              {running ? <><Loader2 size={15} className="animate-spin" /> Analizando...</> : <><FlaskConical size={15} /> Lanzar Motor V5</>}
            </button>
          </div>

          {/* Resultados */}
          {resultado && (
            <div className="flex-1 p-4 space-y-4">
              {/* KPIs globales */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Total plantas', value: fmtNum(totalPlantas), accent: true },
                  { label: 'Área analizada', value: `${fmtNum(totalHa, 2)} ha` },
                  { label: 'Densidad prom.', value: `${fmtNum(densidadProm, 0)} pl/ha` },
                  { label: 'KPI promedio', value: `${fmtNum(kpiProm, 1)}%`, kpi: kpiProm },
                ].map(item => (
                  <div key={item.label} className="rounded-lg p-2.5" style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line-dim)' }}>
                    <p className="text-[10px] mb-0.5" style={{ color: 'var(--ink-faint)' }}>{item.label}</p>
                    <p className="text-sm font-bold" style={{ color: item.kpi != null ? kpiColor(item.kpi) : item.accent ? 'var(--emp-accent-txt)' : 'var(--ink)' }}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Tabla por bloque — estilo C7 */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--ink-faint)' }}>Detalle por Bloque</p>
                <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--line)' }}>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr style={{ background: 'var(--surface-deep)' }}>
                        {['Bloque','Plantas','Área','Densidad','Hileras','KPI'].map(h => (
                          <th key={h} className="px-2 py-1.5 text-left font-semibold" style={{ color: 'var(--ink-faint)', borderBottom: '1px solid var(--line)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {resultado.bloques?.map((b, i) => (
                        <tr key={b.bloque_num ?? i}
                          style={{ background: i % 2 === 0 ? 'var(--surface-raised)' : 'var(--surface-overlay)' }}>
                          <td className="px-2 py-1.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>B{b.bloque_num}</td>
                          <td className="px-2 py-1.5 font-semibold" style={{ color: 'var(--ink)' }}>{fmtNum(b.conteo)}</td>
                          <td className="px-2 py-1.5" style={{ color: 'var(--ink-muted)' }}>{fmtNum(b.area_ha, 2)} ha</td>
                          <td className="px-2 py-1.5" style={{ color: 'var(--ink-muted)' }}>{fmtNum(b.densidad, 0)}</td>
                          <td className="px-2 py-1.5" style={{ color: 'var(--ink-muted)' }}>{fmtNum(b.hileras, 0)}</td>
                          <td className="px-2 py-1.5 font-semibold" style={{ color: kpiColor(b.kpi) }}>
                            {b.kpi != null ? `${fmtNum(b.kpi, 1)}%` : '—'}
                          </td>
                        </tr>
                      ))}
                      {/* Fila totales */}
                      <tr style={{ background: 'var(--surface-deep)', borderTop: '2px solid var(--line)' }}>
                        <td className="px-2 py-1.5 font-bold" style={{ color: 'var(--ink)' }}>Total</td>
                        <td className="px-2 py-1.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{fmtNum(totalPlantas)}</td>
                        <td className="px-2 py-1.5 font-semibold" style={{ color: 'var(--ink)' }}>{fmtNum(totalHa, 2)} ha</td>
                        <td className="px-2 py-1.5 font-semibold" style={{ color: 'var(--ink)' }}>{fmtNum(densidadProm, 0)}</td>
                        <td className="px-2 py-1.5" style={{ color: 'var(--ink-faint)' }}>—</td>
                        <td className="px-2 py-1.5 font-bold" style={{ color: kpiColor(kpiProm) }}>{fmtNum(kpiProm, 1)}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Guardar en bloques */}
              {saved ? (
                <div className="flex items-center gap-2 p-2.5 rounded-lg"
                  style={{ background: 'var(--tone-success-bg)', border: '1px solid var(--tone-success-border)' }}>
                  <CheckCircle2 size={15} style={{ color: 'var(--tone-success-text)' }} />
                  <p className="text-xs font-medium" style={{ color: 'var(--tone-success-text)' }}>
                    Resultados guardados en los bloques del lote
                  </p>
                </div>
              ) : (
                <button onClick={guardarResultados} disabled={saving}
                  className="w-full py-2 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-2"
                  style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)', opacity: saving ? 0.6 : 1 }}>
                  {saving ? <><Loader2 size={14} className="animate-spin" /> Guardando...</> : <><Download size={14} /> Guardar en bloques del lote</>}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Formulario Finca ────────────────────────────────────────────────────────

const EMPTY_FINCA: Omit<EmpFinca, 'id' | 'created_at' | 'updated_at' | 'proveedor'> = {
  empresa_id: 0, nombre: '', codigo: '', ubicacion: '', tipo_finca: 'propia',
  area_ha_perimetro: undefined, activo: true,
};

function FincaForm({
  initial, onSave, onCancel, empresaId,
}: {
  initial?: EmpFinca;
  onSave: (data: Partial<EmpFinca>) => Promise<void>;
  onCancel: () => void;
  empresaId: number;
}) {
  const [form, setForm] = useState({
    empresa_id: empresaId,
    nombre: initial?.nombre ?? '',
    codigo: initial?.codigo ?? '',
    ubicacion: initial?.ubicacion ?? '',
    tipo_finca: initial?.tipo_finca ?? 'propia',
    area_ha_perimetro: initial?.area_ha_perimetro as number | undefined,
    activo: initial?.activo ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({ ...form, nombre: form.nombre.trim() });
    } catch (ex: any) {
      setErr(ex?.message ?? 'Error al guardar.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Nombre *</label>
          <input className={inputCls} value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Ej: Finca La Lucha" />
        </div>
        <div>
          <label className={labelCls}>Código</label>
          <input className={inputCls} value={form.codigo} onChange={e => set('codigo', e.target.value)} placeholder="FL-01" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Tipo de Finca</label>
          <select className={selectCls} value={form.tipo_finca} onChange={e => set('tipo_finca', e.target.value)}>
            <option value="propia">Propia</option>
            <option value="alquilada">Alquilada</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Área total (ha)</label>
          <input className={inputCls} type="number" step="0.01" min="0"
            value={form.area_ha_perimetro ?? ''}
            onChange={e => set('area_ha_perimetro', e.target.value ? parseFloat(e.target.value) : undefined)}
            placeholder="0.00" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Ubicación / Dirección</label>
        <input className={inputCls} value={form.ubicacion} onChange={e => set('ubicacion', e.target.value)} placeholder="Cantón, provincia" />
      </div>
      {err && <p className={errorCls}>{err}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className={btnSecondary} onClick={onCancel}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>
          {saving ? 'Guardando...' : initial ? 'Actualizar Finca' : 'Crear Finca'}
        </button>
      </div>
    </form>
  );
}

// ─── Formulario Lote ─────────────────────────────────────────────────────────

function LoteForm({
  fincaId, initial, onSave, onCancel, empresaId,
}: {
  fincaId: string;
  initial?: EmpLote;
  onSave: (data: Partial<EmpLote>) => Promise<void>;
  onCancel: () => void;
  empresaId: number;
}) {
  const [form, setForm] = useState({
    empresa_id: empresaId,
    finca_id: fincaId,
    numero: initial?.numero ?? '',
    nombre: initial?.nombre ?? '',
    descripcion: initial?.descripcion ?? '',
    area_ha: initial?.area_ha ?? ('' as any),
    area_ha_sembrada: initial?.area_ha_sembrada ?? ('' as any),
    variedad: initial?.variedad ?? 'MD2',
    activo: initial?.activo ?? true,
  });
  const [geojsonRaw, setGeojsonRaw] = useState<string>(
    initial?.geojson ? JSON.stringify(initial.geojson, null, 2) : ''
  );
  const [geojson, setGeojson] = useState<any>(initial?.geojson ?? null);
  const [geojsonErr, setGeojsonErr] = useState('');
  const [drawResetKey, setDrawResetKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ortofotoOpen, setOrtofotoOpen] = useState(false);
  const set = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  // Mapa PNG del lote (origen del diseño del campo)
  const [mapaUrl, setMapaUrl] = useState<string | null>(initial?.mapa_png_url ?? null);
  const [subiendoMapa, setSubiendoMapa] = useState(false);
  const [mapaMsg, setMapaMsg] = useState<string>('');
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  async function subirMapa(file: File) {
    if (!initial?.id) {
      setMapaMsg('Guardá el lote primero antes de subir el mapa.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMapaMsg('El archivo supera 10 MB.');
      return;
    }
    setSubiendoMapa(true);
    setMapaMsg('');
    try {
      // Leer archivo como base64
      const data_base64: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          const base64 = result.includes(',') ? result.split(',')[1] : result;
          resolve(base64);
        };
        reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
        reader.readAsDataURL(file);
      });
      const resp = await fetch(buildApiUrl(`/aplicaciones/lotes/${initial.id}/mapa`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, data_base64 }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
      setMapaUrl(json.url);
      setMapaMsg(`Mapa subido: ${file.name}`);
    } catch (e: any) {
      setMapaMsg(`Error: ${e?.message || 'no se pudo subir'}`);
    } finally {
      setSubiendoMapa(false);
    }
  }

  async function quitarMapa() {
    if (!initial?.id) return;
    setSubiendoMapa(true);
    setMapaMsg('');
    try {
      const resp = await fetch(buildApiUrl(`/aplicaciones/lotes/${initial.id}/mapa`), {
        method: 'DELETE',
      });
      if (!resp.ok) {
        const json = await resp.json().catch(() => ({}));
        throw new Error(json?.error || `HTTP ${resp.status}`);
      }
      setMapaUrl(null);
      setMapaMsg('Mapa quitado del lote.');
    } catch (e: any) {
      setMapaMsg(`Error: ${e?.message || 'no se pudo quitar'}`);
    } finally {
      setSubiendoMapa(false);
    }
  }

  function exportarGeojson() {
    if (!geojson) return;
    const feature = {
      type: 'Feature',
      properties: {
        numero: form.numero,
        nombre: form.nombre,
        area_ha: form.area_ha,
        variedad: form.variedad,
      },
      geometry: geojson,
    };
    const blob = new Blob([JSON.stringify(feature, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lote_${String(form.numero || form.nombre).replace(/\s+/g, '_')}.geojson`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleDrawCommit(geo: any) {
    setGeojson(geo);
    setGeojsonRaw(geo ? JSON.stringify(geo, null, 2) : '');
    setGeojsonErr('');
  }

  function handleRawChangeLote(raw: string) {
    setGeojsonRaw(raw);
    setGeojsonErr('');
    if (!raw.trim()) { setGeojson(null); return; }
    try { setGeojson(JSON.parse(raw)); }
    catch { setGeojsonErr('GeoJSON inválido'); setGeojson(null); }
  }



  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.numero.trim()) { setErr('El número de lote es obligatorio.'); return; }
    if (!form.nombre.trim()) { setErr('El nombre es obligatorio.'); return; }
    if (geojsonErr) { setErr('Corregí el GeoJSON antes de guardar.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        ...form,
        numero: form.numero.trim(),
        nombre: form.nombre.trim(),
        area_ha: form.area_ha !== '' ? parseFloat(String(form.area_ha)) : undefined,
        area_ha_sembrada: form.area_ha_sembrada !== '' ? parseFloat(String(form.area_ha_sembrada)) : undefined,
        geojson: geojson ?? null,
      });
    } catch (ex: any) {
      setErr(ex?.message ?? 'Error al guardar.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Número de Lote *</label>
          <input className={inputCls} value={form.numero} onChange={e => set('numero', e.target.value)} placeholder="Ej: 12, 55A" />
        </div>
        <div>
          <label className={labelCls}>Nombre *</label>
          <input className={inputCls} value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Ej: Lote 12 Norte" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Área total (ha)</label>
          <input className={inputCls} type="number" step="0.01" min="0"
            value={form.area_ha} onChange={e => set('area_ha', e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label className={labelCls}>Área sembrada (ha)</label>
          <input className={inputCls} type="number" step="0.01" min="0"
            value={form.area_ha_sembrada} onChange={e => set('area_ha_sembrada', e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label className={labelCls}>Variedad</label>
          <select className={selectCls} value={form.variedad} onChange={e => set('variedad', e.target.value)}>
            <option value="MD2">MD2 Golden</option>
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls}>Descripción</label>
        <textarea className={inputCls} rows={2} value={form.descripcion}
          onChange={e => set('descripcion', e.target.value)} placeholder="Observaciones del lote..." />
      </div>

      {/* Vista completa ortofoto */}
      {ortofotoOpen && (
        <OrtofotoView
          titulo={`Ortofoto — Lote ${form.numero || 'Nuevo'}`}
          initialGeojson={geojson}
          loteId={initial?.id}
          empresaId={empresaId}
          onConfirm={(geo, pngUrl, bounds) => {
            handleDrawCommit(geo);
            if (pngUrl && bounds && initial?.id) {
              supabase.from('emp_lotes').update({
                raster_png_url: pngUrl,
                raster_bounds: bounds,
                raster_uploaded_at: new Date().toISOString(),
              }).eq('id', initial.id).then(() => {});
            }
            setOrtofotoOpen(false);
          }}
          onCancel={() => setOrtofotoOpen(false)}
        />
      )}

      {/* Mapa */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className={labelCls} style={{ marginBottom: 0 }}>
            <span className="flex items-center gap-1.5"><MapIcon size={12} /> Geometría del Lote</span>
          </label>
          <div className="flex gap-2 flex-wrap justify-end">
            <button type="button" onClick={() => setOrtofotoOpen(true)}
              className="text-[11px] px-2 py-0.5 rounded flex items-center gap-1"
              style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)', border: '1px solid var(--tone-info-border)' }}>
              <ImagePlus size={11} /> Cargar ortofoto de dron
            </button>
            {geojson && (
              <>
                <button type="button" onClick={exportarGeojson}
                  className="text-[11px] px-2 py-0.5 rounded flex items-center gap-1"
                  style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}>
                  <Download size={11} /> Exportar para conteo_7
                </button>
                <button type="button"
                  onClick={() => { setGeojson(null); setGeojsonRaw(''); setGeojsonErr(''); setDrawResetKey(k => k + 1); }}
                  className="text-[11px] px-2 py-0.5 rounded"
                  style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}>
                  Limpiar
                </button>
              </>
            )}
          </div>
        </div>
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--line)' }}>
          <GeoMapPreview
            geojson={geojson}
            height={240}
            maxWidthClassName="max-w-full"
            polygonColor="#38bdf8"
            editable
            drawResetKey={drawResetKey}
            onDrawCommit={handleDrawCommit}
          />
        </div>
        <p className="text-[10px] mt-1" style={{ color: 'var(--ink-faint)' }}>
          Clic en el mapa para marcar vértices · doble clic para cerrar · con 3+ puntos presioná "Usar polígono" · flechas para desplazarte
        </p>
        <textarea
          className={inputCls}
          rows={3}
          value={geojsonRaw}
          onChange={e => handleRawChangeLote(e.target.value)}
          placeholder='{"type":"Polygon","coordinates":[...]} — o dibujá en el mapa'
          style={{ fontFamily: 'monospace', fontSize: '11px', marginTop: '6px' }}
        />
        {geojsonErr && <p className="text-xs mt-1" style={{ color: 'var(--tone-warn-text)' }}>{geojsonErr}</p>}
        {!geojsonErr && geojson && (
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--tone-success-text)' }}>
            ✓ Geometría válida — usá "Exportar para conteo_7" para descargar el GeoJSON y pasárselo al script de análisis
          </p>
        )}
      </div>

      {/* ── Mapa PNG del lote (origen del diseño del campo) ── */}
      {initial?.id && (
        <div className="rounded-lg p-3" style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)' }}>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Mapa del diseño del campo
              </span>
              {mapaUrl && (
                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}>
                  ✓ Cargado
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) subirMapa(f);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                disabled={subiendoMapa}
                onClick={() => fileInputRef.current?.click()}
                className={btnSecondary}
                style={{ fontSize: '11px', padding: '4px 10px' }}>
                {subiendoMapa ? 'Subiendo…' : mapaUrl ? 'Reemplazar' : 'Subir PNG'}
              </button>
              {mapaUrl && !subiendoMapa && (
                <button
                  type="button"
                  onClick={quitarMapa}
                  className={btnSecondary}
                  style={{
                    fontSize: '11px',
                    padding: '4px 10px',
                    background: 'var(--tone-warn-bg)',
                    color: 'var(--tone-warn-text)',
                    border: '1px solid var(--tone-warn-border)',
                  }}>
                  Quitar
                </button>
              )}
            </div>
          </div>
          {mapaUrl ? (
            <a href={mapaUrl} target="_blank" rel="noopener noreferrer" title="Click para ver en tamaño completo">
              <img
                src={mapaUrl}
                alt={`Mapa Lote ${form.numero}`}
                loading="lazy"
                style={{
                  display: 'block',
                  width: '100%',
                  maxHeight: 280,
                  objectFit: 'contain',
                  background: '#ffffff',
                  borderRadius: 6,
                  cursor: 'zoom-in',
                }}
              />
            </a>
          ) : (
            <p className="text-[11px] text-center py-4" style={{ color: 'var(--ink-faint)' }}>
              Sin mapa cargado. Subí un PNG/JPG del diseño del campo (max 10 MB).
            </p>
          )}
          {mapaMsg && (
            <p className="text-[11px] mt-2" style={{
              color: mapaMsg.startsWith('Error') ? 'var(--tone-warn-text)' : 'var(--tone-success-text)',
            }}>
              {mapaMsg}
            </p>
          )}
        </div>
      )}

      {err && <p className={errorCls}>{err}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className={btnSecondary} onClick={onCancel}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>
          {saving ? 'Guardando...' : initial ? 'Actualizar Lote' : 'Crear Lote'}
        </button>
      </div>
    </form>
  );
}

// ─── Formulario Bloque ───────────────────────────────────────────────────────

function BloqueForm({
  loteId, initial, numInicial, onSave, onCancel, empresaId,
}: {
  loteId: string;
  initial?: EmpBloque;
  numInicial?: number;   // número sugerido al crear (cuando no hay initial)
  onSave: (data: Partial<EmpBloque>) => Promise<void>;
  onCancel: () => void;
  empresaId: number;
}) {
  const [form, setForm] = useState({
    empresa_id: empresaId,
    lote_id: loteId,
    num: initial?.num ?? numInicial ?? 1,
    tipo: (initial?.tipo ?? 'siembra') as TipoBloque,
    area_ha: initial?.area_ha ?? ('' as any),
    fecha_siembra: initial?.fecha_siembra?.slice(0, 10) ?? '',
    // Fechas ancla de ciclo (1er y 2º ciclo). Se inicializan como '' (nunca null:
    // al guardar, ''→undefined omite la columna; null la borraría). Ver submit.
    fecha_induccion: initial?.fecha_induccion?.slice(0, 10) ?? '',
    fecha_forza: initial?.fecha_forza?.slice(0, 10) ?? '',
    fecha_maduracion: initial?.fecha_maduracion?.slice(0, 10) ?? '',
    fecha_cosecha: initial?.fecha_cosecha?.slice(0, 10) ?? '',
    fecha_segunda: initial?.fecha_segunda?.slice(0, 10) ?? '',
    fecha_barbecho: initial?.fecha_barbecho?.slice(0, 10) ?? '',
    fecha_semillero: initial?.fecha_semillero?.slice(0, 10) ?? '',
    fecha_induccion2: initial?.fecha_induccion2?.slice(0, 10) ?? '',
    fecha_forza2: initial?.fecha_forza2?.slice(0, 10) ?? '',
    fecha_maduracion2: initial?.fecha_maduracion2?.slice(0, 10) ?? '',
    fecha_cosecha2: initial?.fecha_cosecha2?.slice(0, 10) ?? '',
    ciclo: (initial?.ciclo ?? 'PC') as CicloBloque,
    estado_ciclo: (initial?.estado_ciclo ?? 'preparacion') as EstadoCiclo,
    estadio_vfp: (initial?.estadio_vfp ?? '').toUpperCase(),
    densidad_objetivo: initial?.densidad_objetivo ?? 67000,
    plant_count: initial?.plant_count ?? ('' as any),
    notas: initial?.notas ?? '',
    activo: initial?.activo ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof form, v: any) => setForm(f => ({ ...f, [k]: v }));

  // Catálogo de estadios del ERP (DPC/FPC/DRC/...). Es lo que lee "Estadios de
  // Producción" y el generador de boletas; sin él, el bloque no aparece ahí.
  // `varfecha` = qué fecha de ciclo usa cada estadio como ancla (misma fuente que
  // el generador de boletas), para pedir en el modal justo la fecha que hace falta.
  const [estadiosCfg, setEstadiosCfg] = useState<{ codigo: string; descripcion: string; varfecha: string }[]>([]);
  useEffect(() => {
    supabase.from('app_estadios')
      .select('codigo, descripcion, varfecha, activo')
      .eq('empresa_id', empresaId).eq('activo', true).order('codigo')
      .then(({ data }) => setEstadiosCfg((data ?? []).map((e: any) => ({ codigo: e.codigo, descripcion: e.descripcion, varfecha: e.varfecha }))));
  }, [empresaId]);

  // ── Fecha ancla dinámica según el estadio ──────────────────────────────────
  // Cada estadio usa una fecha de ciclo distinta como ancla para calcular cuándo
  // aplicar (lo lee el generador de boletas de app_estadios.varfecha). El modal
  // pide justo esa fecha para que el bloque no quede "sin fecha ancla".
  const FECHA_LABELS: Record<string, string> = {
    fecha_siembra: 'Siembra',
    fecha_induccion: 'Inducción',
    fecha_forza: 'Forza',
    fecha_maduracion: 'Maduración',
    fecha_cosecha: 'Cosecha',
    fecha_semillero: 'Semillero',
    fecha_barbecho: 'Barbecho',
    fecha_segunda: '2ª Siembra (Segunda)',
    fecha_induccion2: '2ª Inducción',
    fecha_forza2: '2ª Forza',
    fecha_maduracion2: '2ª Maduración',
    fecha_cosecha2: '2ª Cosecha',
  };
  // Fallback COMPLETO por si app_estadios.varfecha viniera vacío (en prod está
  // poblado). Debe coincidir con lo que usa el generador de boletas.
  const FALLBACK_ANCLA: Record<string, string> = {
    PRE: 'fecha_siembra', BAR: 'fecha_siembra', DPC: 'fecha_siembra',
    FPC: 'fecha_induccion', SPC: 'fecha_semillero', SRC: 'fecha_semillero',
    S3RAS: 'fecha_semillero', DRC: 'fecha_segunda', FRC: 'fecha_induccion2',
    MAD: 'fecha_maduracion',
  };
  const varfechaByEstadio = useMemo(() => {
    const m: Record<string, string> = {};
    for (const e of estadiosCfg) {
      if (e.codigo && e.varfecha) m[e.codigo.toUpperCase()] = e.varfecha;
    }
    return m;
  }, [estadiosCfg]);
  const estadioCod = (form.estadio_vfp || '').toUpperCase();
  const anclaField = estadioCod
    ? (varfechaByEstadio[estadioCod] || FALLBACK_ANCLA[estadioCod] || 'fecha_siembra')
    : '';
  const anclaLabel = anclaField ? (FECHA_LABELS[anclaField] || anclaField) : '';
  const anclaVacia = !!anclaField && !((form as any)[anclaField]);

  // Total de plantas sugerido = Área (ha) × Densidad objetivo (pl/ha). Solo si hay área.
  const areaNum = form.area_ha !== '' ? parseFloat(String(form.area_ha)) : NaN;
  const areaValida = Number.isFinite(areaNum) && areaNum > 0;
  const sugeridoPlantas = areaValida && form.densidad_objetivo > 0
    ? Math.round(areaNum * form.densidad_objetivo)
    : null;

  // Densidad y Total de plantas quedan sincronizados por el Área:
  //  - Al escribir la densidad → total = densidad × área
  //  - Al escribir el total    → densidad = total ÷ área
  const onDensidadChange = (raw: string) => {
    const dens = parseInt(raw) || 0;
    setForm(f => {
      const next: any = { ...f, densidad_objetivo: dens };
      if (areaValida && dens > 0) next.plant_count = Math.round(dens * areaNum);
      return next;
    });
  };
  const onPlantasChange = (raw: string) => {
    setForm(f => {
      const next: any = { ...f, plant_count: raw };
      const plantas = raw !== '' ? Number(raw) : NaN;
      if (areaValida && Number.isFinite(plantas) && plantas > 0) {
        next.densidad_objetivo = Math.round(plantas / areaNum);
      }
      return next;
    });
  };
  // Al cambiar el área, el total se recalcula manteniendo la densidad como ancla.
  const onAreaChange = (raw: string) => {
    setForm(f => {
      const next: any = { ...f, area_ha: raw };
      const a = raw !== '' ? parseFloat(raw) : NaN;
      if (Number.isFinite(a) && a > 0 && f.densidad_objetivo > 0) {
        next.plant_count = Math.round(f.densidad_objetivo * a);
      }
      return next;
    });
  };
  // Enter en densidad/plantas: recalcula el otro campo SIN enviar el formulario.
  const onCalcKeyDown = (campo: 'densidad' | 'plantas') => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();   // no dispara submit (guardar)
    if (campo === 'densidad') onDensidadChange(String(form.densidad_objetivo));
    else onPlantasChange(String(form.plant_count));
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.num || form.num < 1) { setErr('El número de bloque es obligatorio.'); return; }
    setSaving(true); setErr('');
    try {
      await onSave({
        ...form,
        num: Number(form.num),
        area_ha: form.area_ha !== '' ? parseFloat(String(form.area_ha)) : undefined,
        // Fechas de ciclo: ''→undefined para que PostgREST OMITA la columna (no la
        // borre) y no falle una columna `date` con string vacío ('' da error 400).
        fecha_siembra: form.fecha_siembra || undefined,
        fecha_induccion: form.fecha_induccion || undefined,
        fecha_forza: form.fecha_forza || undefined,
        fecha_maduracion: form.fecha_maduracion || undefined,
        fecha_cosecha: form.fecha_cosecha || undefined,
        fecha_segunda: form.fecha_segunda || undefined,
        fecha_barbecho: form.fecha_barbecho || undefined,
        fecha_semillero: form.fecha_semillero || undefined,
        fecha_induccion2: form.fecha_induccion2 || undefined,
        fecha_forza2: form.fecha_forza2 || undefined,
        fecha_maduracion2: form.fecha_maduracion2 || undefined,
        fecha_cosecha2: form.fecha_cosecha2 || undefined,
        densidad_objetivo: Number(form.densidad_objetivo),
        plant_count: form.plant_count !== '' ? Number(form.plant_count) : undefined,
        estadio_vfp: form.estadio_vfp || undefined,
      });
    } catch (ex: any) {
      setErr(ex?.message ?? 'Error al guardar.');
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>N° Bloque *</label>
          <input className={inputCls} type="number" min="1" value={form.num}
            onChange={e => set('num', parseInt(e.target.value) || 1)} />
        </div>
        <div>
          <label className={labelCls}>Tipo</label>
          <select className={selectCls} value={form.tipo} onChange={e => set('tipo', e.target.value as TipoBloque)}>
            {(Object.keys(TIPO_BLOQUE_LABEL) as TipoBloque[]).map(t => (
              <option key={t} value={t}>{TIPO_BLOQUE_LABEL[t]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Área (ha)</label>
          <input className={inputCls} type="number" step="0.0001" min="0"
            value={form.area_ha} onChange={e => onAreaChange(e.target.value)} placeholder="0.0000" />
        </div>
      </div>

      <p className={sectionLabelCls}>Datos Agronómicos</p>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>
            Fecha de Siembra
            {anclaField === 'fecha_siembra' && (
              <span className="ml-1 text-[10px]" style={{ color: 'var(--tone-info-text)' }}>· ancla del estadio</span>
            )}
          </label>
          <input className={inputCls} type="date" value={form.fecha_siembra}
            onChange={e => set('fecha_siembra', e.target.value)}
            style={anclaField === 'fecha_siembra' && anclaVacia
              ? { borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' } : undefined} />
          {anclaField === 'fecha_siembra' && anclaVacia && (
            <p className="flex items-center gap-1 mt-1 text-[11px]" style={{ color: 'var(--tone-warn-text)' }}>
              <AlertCircle size={12} /> Este estadio necesita esta fecha para generar la boleta.
            </p>
          )}
        </div>
        <div>
          <label className={labelCls}>Densidad objetivo (pl/ha)</label>
          <input className={inputCls} type="number" min="1" value={form.densidad_objetivo}
            onChange={e => onDensidadChange(e.target.value)}
            onKeyDown={onCalcKeyDown('densidad')} />
        </div>
        <div>
          <label className={labelCls}>Total de plantas</label>
          <input className={inputCls} type="number" min="0" value={form.plant_count}
            onChange={e => onPlantasChange(e.target.value)}
            onKeyDown={onCalcKeyDown('plantas')}
            placeholder={sugeridoPlantas != null ? String(sugeridoPlantas) : '0'} />
          {sugeridoPlantas != null && (
            <button type="button" className="mt-1 text-[11px]" style={{ color: 'var(--emp-accent-txt)' }}
              onClick={() => set('plant_count', sugeridoPlantas)}
              title="Usar Área × Densidad objetivo">
              Sugerido: {sugeridoPlantas.toLocaleString('es-CR')} (Área × Densidad)
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Ciclo</label>
          <select className={selectCls} value={form.ciclo} onChange={e => set('ciclo', e.target.value as CicloBloque)}>
            {(Object.entries(CICLO_LABEL) as [CicloBloque, string][]).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Estadio</label>
          <select className={selectCls} value={form.estadio_vfp} onChange={e => set('estadio_vfp', e.target.value)}>
            <option value="">— Seleccionar —</option>
            {estadiosCfg.map(s => (
              <option key={s.codigo} value={s.codigo.toUpperCase()}>
                {s.descripcion} ({s.codigo.toUpperCase()})
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Fecha ancla del estadio elegido (cuando NO es la siembra, que ya tiene
          su propio campo arriba). El generador de boletas usa esta fecha para
          calcular cuándo aplicar; sin ella el bloque queda "sin fecha ancla". */}
      {anclaField && anclaField !== 'fecha_siembra' && (
        <div>
          <label className={labelCls}>
            {anclaLabel} <span className="text-[10px]" style={{ color: 'var(--tone-info-text)' }}>· ancla del estadio</span>
          </label>
          <input className={inputCls} type="date"
            value={(form as any)[anclaField] || ''}
            onChange={e => set(anclaField as keyof typeof form, e.target.value)}
            style={anclaVacia ? { borderColor: 'var(--tone-warn-border)', background: 'var(--tone-warn-bg)' } : undefined} />
          {anclaVacia && (
            <p className="flex items-center gap-1 mt-1 text-[11px]" style={{ color: 'var(--tone-warn-text)' }}>
              <AlertCircle size={12} /> Este estadio necesita esta fecha para generar la boleta.
            </p>
          )}
        </div>
      )}

      <div>
        <label className={labelCls}>Notas</label>
        <textarea className={inputCls} rows={2} value={form.notas}
          onChange={e => set('notas', e.target.value)} placeholder="Observaciones del bloque..." />
      </div>
      {err && <p className={errorCls}>{err}</p>}
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" className={btnSecondary} onClick={onCancel}>Cancelar</button>
        <button type="submit" className={btnPrimary} disabled={saving}>
          {saving ? 'Guardando...' : initial ? 'Actualizar Bloque' : 'Crear Bloque'}
        </button>
      </div>
    </form>
  );
}

// ─── Fila de Bloque ──────────────────────────────────────────────────────────

function BloqueRow({
  bloque, estadioLabels, onEdit, onDelete, onVerMapa,
}: {
  bloque: EmpBloque;
  estadioLabels: Record<string, string>;
  onEdit: (b: EmpBloque) => void;
  onDelete: (b: EmpBloque) => void;
  onVerMapa: () => void;
}) {
  const dias = diasDesde(bloque.fecha_siembra);
  const esSiembra = bloque.tipo === 'siembra';
  // Estadio real del ERP: "Desarrollo PC (DPC)". Cae al código si no hay descripción.
  const codEstadio = (bloque.estadio_vfp ?? '').toUpperCase();
  const labelEstadio = codEstadio
    ? `${estadioLabels[codEstadio] ?? codEstadio} (${codEstadio})`
    : '';

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors"
      style={{ background: 'var(--surface-deep)', border: '1px solid var(--line-dim)' }}
    >
      <div
        className="w-7 h-7 rounded flex items-center justify-center shrink-0 text-xs font-bold"
        style={{ background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)', border: '1px solid var(--emp-accent)44' }}
      >
        {bloque.num}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium" style={{ color: 'var(--ink)' }}>
            Bloque {bloque.num}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--surface-overlay)', color: 'var(--ink-faint)', border: '1px solid var(--line-dim)' }}>
            {TIPO_BLOQUE_LABEL[bloque.tipo]}
          </span>
          {esSiembra && bloque.ciclo && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)', border: '1px solid var(--tone-info-border)' }}>
              {bloque.ciclo}
            </span>
          )}
          {esSiembra && labelEstadio && (
            <span className="text-[10px] font-semibold" style={{ color: 'var(--emp-accent-txt)' }}>
              {labelEstadio}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          {bloque.area_ha != null && (
            <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{fmtHa(bloque.area_ha)}</span>
          )}
          {esSiembra && bloque.fecha_siembra && (
            <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--ink-faint)' }}>
              <Calendar size={10} />
              Siembra {fmtFecha(bloque.fecha_siembra)}
              {dias != null && <span style={{ color: 'var(--emp-accent-txt)' }}>· día {dias}</span>}
            </span>
          )}
          {esSiembra && bloque.plant_count != null && (
            <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--ink-faint)' }}>
              <Sprout size={10} />
              {bloque.plant_count.toLocaleString('es-CR')} plantas
              {bloque.area_ha != null && bloque.area_ha > 0 && (
                <span style={{ color: 'var(--tone-success-text)' }}>
                  · {(bloque.plant_count / (bloque.area_ha * 10000)).toFixed(2)} pl/m²
                </span>
              )}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <button onClick={onVerMapa} title="Ver en mapa" className="p-1.5 rounded transition-colors"
          style={{ color: 'var(--ink-faint)' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--tone-info-text)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
          <MapIcon size={13} />
        </button>
        <button onClick={() => onEdit(bloque)} className="p-1.5 rounded transition-colors"
          style={{ color: 'var(--ink-faint)' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--emp-accent-txt)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
          <Pencil size={13} />
        </button>
        <button onClick={() => onDelete(bloque)} className="p-1.5 rounded transition-colors"
          style={{ color: 'var(--ink-faint)' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── Panel Lote ───────────────────────────────────────────────────────────────

function LotePanel({
  finca, lote, empresaId, onEditLote, onDeleteLote, onReactivarLote,
}: {
  finca: EmpFinca | null;
  lote: EmpLote;
  empresaId: number;
  onEditLote: (l: EmpLote) => void;
  onDeleteLote: (l: EmpLote) => void;
  onReactivarLote: (l: EmpLote) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [bloques, setBloques] = useState<EmpBloque[]>([]);
  const [loadingB, setLoadingB] = useState(false);
  const [modalBloque, setModalBloque] = useState<EmpBloque | null>(null);
  const [modalCrearBloque, setModalCrearBloque] = useState(false);   // modal "Nuevo Bloque" (sin mapa)
  const [bloqueWorkspaceOpen, setBloqueWorkspaceOpen] = useState(false);
  const [bloqueResaltado, setBloqueResaltado] = useState<number | null>(null);
  const [analisisOpen, setAnalisisOpen] = useState(false);
  const [conteoOpen, setConteoOpen] = useState(false);
  const [deleteBloque, setDeleteBloque] = useState<EmpBloque | null>(null);
  const [croquisOpen, setCroquisOpen] = useState(false);
  const [croquisUrl, setCroquisUrl] = useState<string | null>(lote.mapa_png_url ?? null);
  const [subiendoCroquis, setSubiendoCroquis] = useState(false);
  const [diferenciasCroquis, setDiferenciasCroquis] = useState(0);
  const croquisInputRef = useRef<HTMLInputElement>(null);
  const loaded = useRef(false);
  const croquis = getMapaLote(lote.numero);

  const revisarDiferenciasCroquis = useCallback(async () => {
    if (!croquis) { setDiferenciasCroquis(0); return; }
    const { data } = await supabase.from('emp_bloques')
      .select('num,plant_count,conteo_resultado_payload')
      .eq('lote_id', lote.id).eq('activo', true);
    const porNumero = Object.fromEntries(((data ?? []) as any[]).map(b => [Number(b.num), b]));
    const cantidad = croquis.bloques.filter(b => {
      const registro = porNumero[b.numero];
      const lectura = registro?.conteo_resultado_payload?.mapa_croquis_plantas ?? b.plantas;
      return !registro || registro.plant_count == null || lectura == null || Number(registro.plant_count) !== Number(lectura);
    }).length;
    setDiferenciasCroquis(cantidad);
  }, [croquis, lote.id]);

  useEffect(() => { revisarDiferenciasCroquis(); }, [revisarDiferenciasCroquis]);

  async function cargarCroquisIndividual(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      window.alert('El archivo supera el límite de 10 MB.');
      return;
    }
    setSubiendoCroquis(true);
    try {
      const data_base64: string = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = String(reader.result || '');
          resolve(result.includes(',') ? result.split(',')[1] : result);
        };
        reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
        reader.readAsDataURL(file);
      });
      const resp = await fetch(buildApiUrl(`/aplicaciones/lotes/${lote.id}/mapa`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename: file.name, data_base64 }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
      setCroquisUrl(`${json.url}${json.url.includes('?') ? '&' : '?'}t=${Date.now()}`);
      setCroquisOpen(true);
    } catch (e: any) {
      window.alert(`No se pudo cargar el croquis: ${e?.message || 'error desconocido'}`);
    } finally {
      setSubiendoCroquis(false);
    }
  }

  // Catálogo de estadios (código → descripción) para mostrar "Desarrollo PC (DPC)"
  // en la fila del bloque, en lugar del campo obsoleto estado_ciclo.
  const [estadioLabels, setEstadioLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    supabase.from('app_estadios')
      .select('codigo, descripcion, activo')
      .eq('empresa_id', empresaId).eq('activo', true)
      .then(({ data }) => {
        const m: Record<string, string> = {};
        for (const e of (data ?? []) as any[]) m[String(e.codigo).toUpperCase()] = e.descripcion;
        setEstadioLabels(m);
      });
  }, [empresaId]);

  // Cerrar croquis con tecla Esc
  useEffect(() => {
    if (!croquisOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setCroquisOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [croquisOpen]);

  const cargarBloques = useCallback(async () => {
    setLoadingB(true);
    const { data } = await supabase
      .from('emp_bloques')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('lote_id', lote.id)
      .order('num');
    setBloques(data ?? []);
    setLoadingB(false);
  }, [empresaId, lote.id]);

  useEffect(() => {
    if (expanded && !loaded.current) {
      loaded.current = true;
      cargarBloques();
    }
  }, [expanded, cargarBloques]);

  async function saveBloque(data: Partial<EmpBloque>) {
    if (modalBloque) {
      await supabase.from('emp_bloques').update(data).eq('id', modalBloque.id);
    } else {
      await supabase.from('emp_bloques').insert({ ...data, empresa_id: empresaId, lote_id: lote.id });
    }
    setModalBloque(null);
    setModalCrearBloque(false);
    loaded.current = false;
    await cargarBloques();
    loaded.current = true;
  }

  // Siguiente número de bloque sugerido para "Nuevo Bloque".
  const siguienteNumBloque = bloques.length === 0 ? 1 : Math.max(...bloques.map(b => b.num)) + 1;

  async function confirmDeleteBloque() {
    if (!deleteBloque) return;
    await supabase.from('emp_bloques').delete().eq('id', deleteBloque.id);
    setDeleteBloque(null);
    loaded.current = false;
    await cargarBloques();
    loaded.current = true;
  }

  const totalHa = bloques.filter(b => b.tipo === 'siembra').reduce((s, b) => s + (b.area_ha ?? 0), 0);
  const totalPlantas = bloques.reduce((s, b) => s + (b.plant_count ?? 0), 0);
  const plM2 = lote.densidad_m2 ?? (totalHa > 0 && totalPlantas > 0 ? totalPlantas / (totalHa * 10000) : null);
  const plHa = plM2 != null ? plM2 * 10000 : null;

  return (
    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--line)' }}>
      {/* Header del lote */}
      <div
        role="button"
        tabIndex={0}
        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer"
        style={{ background: 'var(--surface-raised)' }}
        onClick={() => setExpanded(e => !e)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(x => !x); } }}
      >
        <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ background: 'var(--emp-accent-bg)' }}>
          {expanded ? <ChevronDown size={14} style={{ color: 'var(--emp-accent-txt)' }} /> : <ChevronRight size={14} style={{ color: 'var(--emp-accent-txt)' }} />}
        </div>
        <Layers size={15} style={{ color: 'var(--emp-accent-txt)', flexShrink: 0 }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold" style={{ color: 'var(--ink)' }}>
              Lote {lote.numero} — {lote.nombre}
            </span>
            {lote.activo === false && (
              <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}>
                Inactivo
              </span>
            )}
            {lote.variedad && (
              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}>
                {lote.variedad}
              </span>
            )}
            {lote.geojson && (
              <span className="text-[10px] flex items-center gap-0.5 px-1.5 py-0.5 rounded" style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)', border: '1px solid var(--tone-info-border)' }}>
                <Globe size={9} />GeoJSON
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {finca && (
              <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--ink-faint)' }}>
                <Building2 size={9} />{finca.nombre}
              </span>
            )}
            {lote.area_ha != null && <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{fmtHa(lote.area_ha)}</span>}
            {expanded && totalHa > 0 && <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{fmtHa(totalHa)} sembradas</span>}
            {expanded && totalPlantas > 0 && (
              <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--ink-faint)' }}>
                <Sprout size={10} />{totalPlantas.toLocaleString('es-CR')} plantas
              </span>
            )}
            {expanded && plM2 != null && lote.calibrado && (
              <span className="text-[11px] flex items-center gap-1 font-medium" style={{ color: 'var(--tone-success-text)' }}>
                <Leaf size={9} />{plM2.toFixed(2)} pl/m² · {Math.round(plHa!).toLocaleString('es-CR')} pl/ha
              </span>
            )}
            {!expanded && <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{bloques.length > 0 ? `${bloques.length} bloques` : 'Click para expandir'}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
          <div className="flex w-10 shrink-0 justify-end">
            {croquis && diferenciasCroquis > 0 && (
              <button
                onClick={() => setCroquisOpen(true)}
                title={`${diferenciasCroquis} bloque${diferenciasCroquis === 1 ? '' : 's'} con diferencia entre croquis y conteo manual`}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold"
                style={{ background: 'rgba(239,68,68,0.13)', color: '#f87171', border: '1px solid rgba(239,68,68,0.55)' }}>
                <AlertCircle size={13} /> {diferenciasCroquis}
              </button>
            )}
          </div>
          {(croquis || croquisUrl) ? (
            <button
              onClick={() => setCroquisOpen(true)}
              title={croquis ? 'Ver y corregir el croquis interactivo' : 'Ver croquis del lote'}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold"
              style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)', border: '1px solid var(--tone-info-border)' }}
              onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
              <Layers size={12} /> Croquis
            </button>
          ) : (
            <button
              onClick={() => croquisInputRef.current?.click()}
              disabled={subiendoCroquis}
              title="Cargar el croquis de este lote"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold"
              style={{ background: 'rgba(139, 92, 246, 0.14)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.55)', opacity: subiendoCroquis ? 0.65 : 1 }}>
              {subiendoCroquis ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {subiendoCroquis ? 'Cargando…' : 'Cargar croquis'}
            </button>
          )}
          <input ref={croquisInputRef} type="file" accept="image/png,image/jpeg" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) cargarCroquisIndividual(f); e.target.value = ''; }} />
          <button
            onClick={() => setConteoOpen(true)}
            title="Conteo calibrado de plantas"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold"
            style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
            <Leaf size={12} /> Conteo
          </button>
          <button
            onClick={async () => {
              // Asegurar que los bloques estén cargados antes de abrir el análisis
              if (!loaded.current) {
                loaded.current = true;
                await cargarBloques();
              }
              setAnalisisOpen(true);
            }}
            title="Análisis — Mapa de calor"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold"
            style={{ background: 'var(--tone-warn-bg)', color: 'var(--tone-warn-text)', border: '1px solid var(--tone-warn-border)' }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}>
            <BarChart2 size={12} /> Análisis
          </button>
          <button onClick={() => onEditLote(lote)} className="p-1.5 rounded"
            style={{ color: 'var(--ink-faint)' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--emp-accent-txt)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
            <Pencil size={13} />
          </button>
          {lote.activo === false ? (
            <button onClick={() => onReactivarLote(lote)} className="p-1.5 rounded"
              title="Reactivar lote"
              style={{ color: 'var(--ink-faint)' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--tone-success-text)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
              <RotateCcw size={13} />
            </button>
          ) : (
            <button onClick={() => onDeleteLote(lote)} className="p-1.5 rounded"
              title="Desactivar lote"
              style={{ color: 'var(--ink-faint)' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Bloques */}
      {expanded && (
        <div className="p-4 space-y-2" style={{ background: 'var(--surface-base)', borderTop: '1px solid var(--line-dim)' }}>
          {loadingB ? (
            <p className="text-xs text-center py-3" style={{ color: 'var(--ink-faint)' }}>Cargando bloques...</p>
          ) : bloques.length === 0 ? (
            <p className="text-xs text-center py-3" style={{ color: 'var(--ink-faint)' }}>Sin bloques. Agregá el primero.</p>
          ) : (
            bloques.map(b => (
              <BloqueRow key={b.id} bloque={b} estadioLabels={estadioLabels}
                onEdit={b => setModalBloque(b)}
                onDelete={b => setDeleteBloque(b)}
                onVerMapa={() => { setBloqueResaltado(b.num); setBloqueWorkspaceOpen(true); }} />
            ))
          )}
          <button
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded text-xs font-medium transition-colors mt-1"
            style={{ border: '1px dashed var(--line)', color: 'var(--ink-faint)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--emp-accent-txt)'; e.currentTarget.style.borderColor = 'var(--emp-accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-faint)'; e.currentTarget.style.borderColor = 'var(--line)'; }}
            onClick={() => setModalCrearBloque(true)}
          >
            <Plus size={13} /> Agregar Bloque
          </button>
        </div>
      )}

      {/* BloqueWorkspace — paso 2 */}
      {bloqueWorkspaceOpen && (
        <BloqueWorkspace
          finca={finca}
          lote={lote}
          empresaId={empresaId}
          bloqueResaltado={bloqueResaltado}
          onClose={async () => {
            setBloqueWorkspaceOpen(false);
            setBloqueResaltado(null);
            setExpanded(true);
            await cargarBloques();
          }}
          onSaved={async () => {
            setBloqueWorkspaceOpen(false);
            setBloqueResaltado(null);
            setExpanded(true);
            await cargarBloques();
          }}
        />
      )}

      {/* Conteo calibrado */}
      {conteoOpen && (
        <ConteoCalibradoPage
          initialFincaId={finca.id}
          initialLoteId={lote.id}
          onClose={() => { setConteoOpen(false); cargarBloques(); }}
        />
      )}

      {/* Análisis C7 — mapa de calor full-screen */}
      {analisisOpen && (
        <ConteoPlantasV8Page
          fincaId={lote.id}
          rasterPngUrl={lote.raster_png_url ?? undefined}
          rasterBounds={lote.raster_bounds ?? undefined}
          nombreLote={`Lote ${lote.numero} — ${lote.nombre}`}
          geojsonLote={lote.geojson}
          empresaId={empresaId}
          bloquesDB={bloques}
          onClose={() => setAnalisisOpen(false)}
        />
      )}

      {/* Modal del mapa PNG del lote (diseño del campo) */}
      {croquisOpen && croquis && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[9999] flex flex-col p-4"
          style={{ background: 'rgba(0,0,0,0.88)' }}
          onClick={() => setCroquisOpen(false)}>
          <div
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl"
            style={{ background: 'var(--surface-base)', border: '1px solid var(--line)' }}
            onClick={e => e.stopPropagation()}>
            <div
              className="flex shrink-0 items-center justify-between gap-3 px-4 py-3"
              style={{ background: 'var(--surface-raised)', borderBottom: '1px solid var(--line)' }}>
              <div className="flex items-center gap-2">
                <Layers size={17} style={{ color: 'var(--tone-info-text)' }} />
                <span className="text-sm font-bold" style={{ color: 'var(--ink)' }}>
                  Croquis interactivo — Lote {lote.numero} — {lote.nombre}
                </span>
              </div>
              <button
                onClick={() => setCroquisOpen(false)}
                className="rounded-lg p-2"
                style={{ color: 'var(--ink-muted)', border: '1px solid var(--line)' }}
                title="Cerrar">
                <X size={18} />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <MapaLoteInteractivo mapa={croquis} loteId={lote.id} onDataChange={revisarDiferenciasCroquis} />
            </div>
          </div>
        </div>,
        document.body
      )}

      {croquisOpen && !croquis && croquisUrl && ReactDOM.createPortal(
        <div
          onClick={() => setCroquisOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px', cursor: 'zoom-out',
          }}>
          <div onClick={e => e.stopPropagation()} style={{
            position: 'relative',
            maxWidth: '95vw', maxHeight: '95vh',
            background: '#ffffff', borderRadius: 10,
            boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
            display: 'flex', flexDirection: 'column',
          }}>
            <div style={{
              padding: '8px 14px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#f8fafc', borderRadius: '10px 10px 0 0',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <MapIcon size={14} style={{ color: '#16a34a' }} />
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                  Mapa Lote {lote.numero} — {lote.nombre}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <a href={croquisUrl} target="_blank" rel="noopener noreferrer"
                  title="Abrir en tamaño completo en otra pestaña"
                  style={{
                    fontSize: 11, color: '#16a34a', textDecoration: 'none',
                    padding: '4px 10px', borderRadius: 6,
                    border: '1px solid #16a34a44', background: '#16a34a11',
                  }}>
                  Tamaño completo ↗
                </a>
                <button
                  onClick={() => setCroquisOpen(false)}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: '#64748b', padding: 4, lineHeight: 1,
                  }}
                  title="Cerrar (Esc)">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div style={{ overflow: 'auto', flex: 1, padding: '12px', background: '#ffffff' }}>
              <img
                src={croquisUrl}
                alt={`Croquis Lote ${lote.numero}`}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: 'calc(95vh - 80px)',
                  objectFit: 'contain',
                  margin: '0 auto',
                }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal edición de bloque existente */}
      {modalBloque !== null && (
        <Modal
          title={`Editar Bloque ${modalBloque.num}`}
          onClose={() => setModalBloque(null)}
        >
          <BloqueForm
            loteId={lote.id}
            empresaId={empresaId}
            initial={modalBloque}
            onSave={saveBloque}
            onCancel={() => setModalBloque(null)}
          />
        </Modal>
      )}

      {/* Modal nuevo bloque — mismo formulario completo, sin pasar por el mapa */}
      {modalCrearBloque && (
        <Modal
          title="Nuevo Bloque"
          onClose={() => setModalCrearBloque(false)}
        >
          <BloqueForm
            loteId={lote.id}
            empresaId={empresaId}
            numInicial={siguienteNumBloque}
            onSave={saveBloque}
            onCancel={() => setModalCrearBloque(false)}
          />
        </Modal>
      )}

      {/* Confirm delete bloque */}
      {deleteBloque && (
        <Modal title="Eliminar Bloque" onClose={() => setDeleteBloque(null)}>
          <div className="space-y-4">
            <div className="flex gap-3 p-3 rounded" style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
              <AlertCircle size={16} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <p className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>
                ¿Eliminar el <strong>Bloque {deleteBloque.num}</strong>? Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button className={btnSecondary} onClick={() => setDeleteBloque(null)}>Cancelar</button>
              <button className={btnDanger} onClick={confirmDeleteBloque}>Eliminar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Panel Finca ──────────────────────────────────────────────────────────────

function FincaPanel({
  finca, empresaId, onEdit, onDelete,
}: {
  finca: EmpFinca;
  empresaId: number;
  onEdit: (f: EmpFinca) => void;
  onDelete: (f: EmpFinca) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [lotes, setLotes] = useState<EmpLote[]>([]);
  const [loadingL, setLoadingL] = useState(false);
  const [workspaceLote, setWorkspaceLote] = useState<'create' | EmpLote | null>(null);
  const [deleteLote, setDeleteLote] = useState<EmpLote | null>(null);
  const loaded = useRef(false);

  const cargarLotes = useCallback(async () => {
    setLoadingL(true);
    const { data } = await supabase
      .from('emp_lotes')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('finca_id', finca.id)
      .eq('activo', true)
      .order('numero');
    setLotes(data ?? []);
    setLoadingL(false);
  }, [empresaId, finca.id]);

  useEffect(() => {
    if (expanded && !loaded.current) {
      loaded.current = true;
      cargarLotes();
    }
  }, [expanded, cargarLotes]);

  async function onLoteSaved() {
    setWorkspaceLote(null);
    loaded.current = false;
    await cargarLotes();
    loaded.current = true;
  }

  async function confirmDeleteLote() {
    if (!deleteLote) return;
    await supabase.from('emp_lotes').update({ activo: false }).eq('id', deleteLote.id);
    setDeleteLote(null);
    loaded.current = false;
    await cargarLotes();
    loaded.current = true;
  }

  async function reactivarLote(l: EmpLote) {
    await supabase.from('emp_lotes').update({ activo: true }).eq('id', l.id);
    loaded.current = false;
    await cargarLotes();
    loaded.current = true;
  }

  return (
    <div className={`${cardCls} overflow-hidden`}>
      {/* Header finca */}
      <div
        role="button"
        tabIndex={0}
        className="w-full flex items-center gap-3 px-5 py-4 text-left transition-colors cursor-pointer"
        style={{ background: 'var(--surface-raised)' }}
        onClick={() => setExpanded(e => !e)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(x => !x); } }}
      >
        <Building2 size={18} style={{ color: 'var(--emp-accent-txt)', flexShrink: 0 }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{finca.nombre}</span>
            {finca.codigo && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded"
                style={{ background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)', border: '1px solid var(--emp-accent)44' }}>
                {finca.codigo}
              </span>
            )}
            <span className="text-[10px] px-1.5 py-0.5 rounded capitalize"
              style={{ background: 'var(--surface-overlay)', color: 'var(--ink-faint)', border: '1px solid var(--line-dim)' }}>
              {finca.tipo_finca ?? 'propia'}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {finca.area_ha_perimetro != null && (
              <span className="text-[11px] flex items-center gap-1" style={{ color: 'var(--ink-faint)' }}>
                <MapPin size={10} />{fmtHa(finca.area_ha_perimetro)}
              </span>
            )}
            {finca.ubicacion && (
              <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>{finca.ubicacion}</span>
            )}
            <span className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
              {expanded ? `${lotes.length} lote(s)` : 'Click para ver lotes'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0" onClick={e => e.stopPropagation()}>
          {finca.geojson && (
            <button
              title="Exportar GeoJSON para conteo_7.py"
              className="p-1.5 rounded"
              style={{ color: 'var(--ink-faint)' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--tone-info-text)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}
              onClick={() => {
                const geo = {
                  type: 'Feature',
                  properties: { nombre: finca.nombre, codigo: finca.codigo, id: finca.id },
                  geometry: finca.geojson,
                };
                const blob = new Blob([JSON.stringify(geo, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `lote_${(finca.codigo || finca.nombre).replace(/\s+/g, '_')}.geojson`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Download size={14} />
            </button>
          )}
          <button onClick={() => onEdit(finca)} className="p-1.5 rounded"
            style={{ color: 'var(--ink-faint)' }}
            onMouseEnter={e => (e.currentTarget.style.color = 'var(--emp-accent-txt)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
            <Pencil size={14} />
          </button>
          <button onClick={() => onDelete(finca)} className="p-1.5 rounded"
            style={{ color: 'var(--ink-faint)' }}
            onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
            onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
            <Trash2 size={14} />
          </button>
          <div style={{ color: 'var(--ink-faint)' }}>
            {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </div>
        </div>
      </div>

      {/* Workspace unificado lote */}
      {workspaceLote !== null && (
        <LoteWorkspace
          finca={finca}
          lote={workspaceLote !== 'create' ? workspaceLote as EmpLote : undefined}
          empresaId={empresaId}
          onSaved={onLoteSaved}
          onClose={() => setWorkspaceLote(null)}
        />
      )}

      {/* Lotes */}
      {expanded && (
        <div className="p-4 space-y-3" style={{ borderTop: '1px solid var(--line)' }}>
          {loadingL ? (
            <p className="text-xs text-center py-4" style={{ color: 'var(--ink-faint)' }}>Cargando lotes...</p>
          ) : lotes.length === 0 ? (
            <p className="text-xs text-center py-4" style={{ color: 'var(--ink-faint)' }}>Sin lotes. Creá el primero.</p>
          ) : (
            lotes.map(l => (
              <LotePanel key={l.id} finca={finca} lote={l} empresaId={empresaId}
                onEditLote={l => setWorkspaceLote(l)}
                onDeleteLote={l => setDeleteLote(l)}
                onReactivarLote={reactivarLote} />
            ))
          )}
          <button
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded text-xs font-medium transition-colors"
            style={{ border: '1px dashed var(--line)', color: 'var(--ink-faint)' }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--emp-accent-txt)'; e.currentTarget.style.borderColor = 'var(--emp-accent)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-faint)'; e.currentTarget.style.borderColor = 'var(--line)'; }}
            onClick={() => setWorkspaceLote('create')}
          >
            <Plus size={13} /> Agregar Lote
          </button>
        </div>
      )}


      {/* Confirm delete lote */}
      {deleteLote && (
        <Modal title="Desactivar Lote" onClose={() => setDeleteLote(null)}>
          <div className="space-y-4">
            <div className="flex gap-3 p-3 rounded" style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
              <AlertCircle size={16} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <p className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>
                ¿Desactivar el <strong>Lote {deleteLote.numero} — {deleteLote.nombre}</strong>? Los bloques quedan intactos.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button className={btnSecondary} onClick={() => setDeleteLote(null)}>Cancelar</button>
              <button className={btnDanger} onClick={confirmDeleteLote}>Desactivar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Página Principal — Lote / Bloque ────────────────────────────────────────

export function FincaLoteBloquePage() {
  const empresaId = useEmpresaId();

  // Catálogo de fincas (para mostrar nombre en cada lote + filtro opcional)
  const [fincas, setFincas] = useState<EmpFinca[]>([]);
  const [fincaId, setFincaId] = useState<string>('');     // '' = todas las fincas
  const [loadingFincas, setLoadingFincas] = useState(true);

  // Todos los lotes activos de la empresa (se carga 1 vez)
  const [lotes, setLotes] = useState<EmpLote[]>([]);
  const [loadingLotes, setLoadingLotes] = useState(false);
  const [workspaceLote, setWorkspaceLote] = useState<'create' | EmpLote | null>(null);
  const [deleteLote, setDeleteLote] = useState<EmpLote | null>(null);
  const loadedRef = useRef(false);

  // Buscador por número/nombre de lote + filtro opcional por finca
  const [busquedaLote, setBusquedaLote] = useState('');
  // Mostrar también los lotes desactivados (soft-delete)
  const [verInactivos, setVerInactivos] = useState(false);
  // Aviso "no hay inactivos": sobrevive al auto-desmarcado del check
  const [avisoSinInactivos, setAvisoSinInactivos] = useState(false);
  const fincaPorId = useMemo(() => {
    const m: Record<string, EmpFinca> = {};
    for (const f of fincas) m[f.id] = f;
    return m;
  }, [fincas]);

  // Los inactivos ya vienen cargados: el check solo los muestra u oculta,
  // sin volver a consultar (toggle instantáneo).
  const lotesVisibles = useMemo(
    () => (verInactivos ? lotes : lotes.filter(l => l.activo !== false)),
    [lotes, verInactivos],
  );

  const lotesFiltrados = useMemo(() => {
    const q = busquedaLote.trim().toLowerCase();
    return lotesVisibles.filter(l => {
      if (fincaId && l.finca_id !== fincaId) return false;
      if (!q) return true;
      return String(l.numero ?? '').toLowerCase().includes(q)
        || String(l.nombre ?? '').toLowerCase().includes(q);
    });
  }, [lotesVisibles, busquedaLote, fincaId]);

  const lotesInactivos = useMemo(() => lotes.filter(l => l.activo === false).length, [lotes]);

  const fincaActual = useMemo(() => fincas.find(f => f.id === fincaId) ?? null, [fincas, fincaId]);

  // Cargar fincas al montar
  useEffect(() => {
    async function cargar() {
      setLoadingFincas(true);
      const { data } = await supabase
        .from('emp_fincas')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('activo', true)
        .order('nombre');
      const lista = data ?? [];
      setFincas(lista);
      // Por defecto: TODAS las fincas (fincaId = ''). El usuario filtra opcionalmente.
      setLoadingFincas(false);
    }
    cargar();
  }, [empresaId]);

  // Cargar TODOS los lotes de la empresa, activos e inactivos (sin filtrar por
  // finca). El filtro de "Ver inactivos" se resuelve en memoria, no en la red.
  const cargarLotes = useCallback(async () => {
    setLoadingLotes(true);
    const { data } = await supabase
      .from('emp_lotes')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('numero');
    setLotes(data ?? []);
    setLoadingLotes(false);
  }, [empresaId]);

  useEffect(() => {
    loadedRef.current = false;
    cargarLotes().then(() => { loadedRef.current = true; });
  }, [cargarLotes]);

  // Si se pidió ver inactivos y no hay ninguno, el check se desmarca solo:
  // queda el aviso, no un filtro encendido que no hace nada.
  useEffect(() => {
    if (verInactivos && !loadingLotes && lotesInactivos === 0) {
      setVerInactivos(false);
      setAvisoSinInactivos(true);
    }
  }, [verInactivos, loadingLotes, lotesInactivos]);

  // El aviso comparte renglón con el contador: se retira solo para devolverlo.
  useEffect(() => {
    if (!avisoSinInactivos) return;
    const t = setTimeout(() => setAvisoSinInactivos(false), 4000);
    return () => clearTimeout(t);
  }, [avisoSinInactivos]);

  async function onLoteSaved() {
    setWorkspaceLote(null);
    await cargarLotes();
  }

  async function confirmDeleteLote() {
    if (!deleteLote) return;
    await supabase.from('emp_lotes').update({ activo: false }).eq('id', deleteLote.id);
    setDeleteLote(null);
    await cargarLotes();
  }

  async function reactivarLote(l: EmpLote) {
    await supabase.from('emp_lotes').update({ activo: true }).eq('id', l.id);
    await cargarLotes();
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">

      {/* Encabezado */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className={pageTitleCls}>Catálogo — Lote / Bloque</h1>
          <p className={pageSubtitleCls}>Estructura territorial del cultivo de piña MD2</p>
        </div>
        <button
          className={btnPrimary}
          disabled={fincas.length === 0}
          onClick={() => {
            // Al crear, si no hay finca seleccionada, usar la primera disponible
            if (!fincaId && fincas.length > 0) setFincaId(fincas[0].id);
            setWorkspaceLote('create');
          }}>
          <span className="flex items-center gap-1.5"><Plus size={14} /> Agregar Lote</span>
        </button>
      </div>

      {/* Buscador de lote (primario) + filtro opcional por finca */}
      <div className="flex items-center gap-3 mb-6 p-4 rounded-xl flex-wrap"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)' }}>
        <Layers size={16} style={{ color: 'var(--emp-accent-txt)', flexShrink: 0 }} />
        <label className="text-sm font-medium shrink-0" style={{ color: 'var(--ink-muted)' }}>Buscar lote:</label>
        <input
          type="text"
          className={inputCls}
          style={{ width: '14rem', fontSize: '14px' }}
          placeholder="N° o nombre del lote…"
          value={busquedaLote}
          onChange={e => setBusquedaLote(e.target.value)}
          autoFocus
        />
        {busquedaLote && (
          <button
            type="button"
            onClick={() => setBusquedaLote('')}
            title="Limpiar búsqueda"
            className="p-1 rounded"
            style={{ color: 'var(--ink-faint)', background: 'transparent', border: '1px solid var(--line)' }}>
            <X size={12} />
          </button>
        )}

        {/* Filtro opcional por finca */}
        <div className="flex items-center gap-2 ml-2 pl-3" style={{ borderLeft: '1px solid var(--line)' }}>
          <Building2 size={14} style={{ color: 'var(--ink-muted)' }} />
          <label className="text-xs font-medium shrink-0" style={{ color: 'var(--ink-muted)' }}>Finca:</label>
          {loadingFincas ? (
            <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Cargando…</span>
          ) : (
            <select
              className={selectCls}
              style={{ maxWidth: 260, fontSize: '13px' }}
              value={fincaId}
              onChange={e => setFincaId(e.target.value)}>
              <option value="">Todas las fincas</option>
              {fincas.map(f => (
                <option key={f.id} value={f.id}>
                  {f.nombre}{f.codigo ? ` (${f.codigo})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Mostrar lotes desactivados */}
        <label className="flex items-center gap-1.5 ml-2 pl-3 text-xs font-medium cursor-pointer select-none"
          style={{ borderLeft: '1px solid var(--line)', color: 'var(--ink-muted)' }}
          title="Incluir en la lista los lotes desactivados">
          <input
            type="checkbox"
            checked={verInactivos}
            onChange={e => { setVerInactivos(e.target.checked); if (e.target.checked) setAvisoSinInactivos(false); }}
            style={{ accentColor: 'var(--emp-accent)' }}
          />
          Ver inactivos
        </label>

        {/* Contador y aviso comparten renglón: el aviso no debe empujar la lista */}
        {avisoSinInactivos ? (
          <span className="text-xs ml-auto flex items-center gap-1.5" style={{ color: 'var(--tone-info-text)' }}>
            <AlertCircle size={12} style={{ flexShrink: 0 }} />
            No hay lotes inactivos
          </span>
        ) : (
          <span className="text-xs ml-auto" style={{ color: 'var(--ink-faint)' }}>
            {(busquedaLote || fincaId)
              ? `${lotesFiltrados.length} / ${lotesVisibles.length} lote${lotesVisibles.length === 1 ? '' : 's'}`
              : `${lotesVisibles.length} lote${lotesVisibles.length === 1 ? '' : 's'}`}
            {verInactivos && lotesInactivos > 0 && ` · ${lotesInactivos} inactivo${lotesInactivos === 1 ? '' : 's'}`}
          </span>
        )}
      </div>

      {/* Lista de lotes */}
      {loadingLotes ? (
        <div className="flex flex-col items-center py-16 gap-3">
          <div className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: 'var(--emp-accent)' }} />
          <p className="text-xs" style={{ color: 'var(--ink-faint)' }}>Cargando lotes...</p>
        </div>
      ) : lotesVisibles.length === 0 ? (
        <div className="flex flex-col items-center py-16 gap-3 rounded-xl"
          style={{ border: '1px dashed var(--line)' }}>
          <Layers size={36} style={{ color: 'var(--ink-faint)', opacity: 0.3 }} />
          <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>Sin lotes registrados.</p>
          <button className={btnPrimary} disabled={fincas.length === 0} onClick={() => {
            if (!fincaId && fincas.length > 0) setFincaId(fincas[0].id);
            setWorkspaceLote('create');
          }}>
            <span className="flex items-center gap-1.5"><Plus size={14} /> Agregar primer Lote</span>
          </button>
        </div>
      ) : lotesFiltrados.length === 0 ? (
        <div className="flex flex-col items-center py-12 gap-2 rounded-xl"
          style={{ border: '1px dashed var(--line)' }}>
          <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>
            Ningún lote coincide con los filtros.
          </p>
          <div className="flex gap-2">
            {busquedaLote && <button className={btnSecondary} onClick={() => setBusquedaLote('')}>Limpiar búsqueda</button>}
            {fincaId && <button className={btnSecondary} onClick={() => setFincaId('')}>Ver todas las fincas</button>}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {lotesFiltrados.map(l => (
            <LotePanel
              key={l.id}
              finca={fincaPorId[l.finca_id] ?? null}
              lote={l}
              empresaId={empresaId}
              onEditLote={l => setWorkspaceLote(l)}
              onDeleteLote={l => setDeleteLote(l)}
              onReactivarLote={reactivarLote}
            />
          ))}
        </div>
      )}

      {/* LoteWorkspace — paso 1 */}
      {workspaceLote !== null && (() => {
        const fincaParaWorkspace = workspaceLote === 'create'
          ? fincaActual
          : (fincaPorId[(workspaceLote as EmpLote).finca_id] ?? null);
        if (!fincaParaWorkspace) return null;
        return (
          <LoteWorkspace
            finca={fincaParaWorkspace}
            lote={workspaceLote !== 'create' ? workspaceLote as EmpLote : undefined}
            empresaId={empresaId}
            onSaved={onLoteSaved}
            onClose={() => setWorkspaceLote(null)}
          />
        );
      })()}

      {/* Confirm desactivar lote */}
      {deleteLote && (
        <Modal title="Desactivar Lote" onClose={() => setDeleteLote(null)}>
          <div className="space-y-4">
            <div className="flex gap-3 p-3 rounded" style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)' }}>
              <AlertCircle size={16} style={{ color: 'var(--tone-warn-text)', flexShrink: 0, marginTop: 1 }} />
              <p className="text-sm" style={{ color: 'var(--tone-warn-ink)' }}>
                ¿Desactivar el <strong>Lote {deleteLote.numero} — {deleteLote.nombre}</strong>? Los bloques quedan intactos.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button className={btnSecondary} onClick={() => setDeleteLote(null)}>Cancelar</button>
              <button className={btnDanger} onClick={confirmDeleteLote}>Desactivar</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
